/**
 * Internal-only customer settings. Sections save independently: a typo in
 * one must not block a fix in another, and one PATCH carrying five
 * unrelated changes makes the audit diff unreadable. See
 * docs/superpowers/specs/2026-08-18-portal-customer-settings-design.md §6.2.
 *
 * Identity and FINTRAC save directly, with no dry-run preview, on the
 * per-section save pattern (`useSectionSave`) that the remaining sections
 * (SFTP keys, F2R credentials, ...) reuse. Reporting is different: it can
 * change what FINTRAC filings are already in flight, so Save previews the
 * blast radius (`previewCustomerConfig`) and only applies
 * (`patchCustomerConfig`) after the operator confirms.
 *
 * Presentation reuses the onboarding form's vocabulary (`.form-card`,
 * `.form-section`, `.form-row` -- see theme.css) rather than a second one,
 * with one addition this page needs and onboarding does not: every card
 * carries its own footer showing that section's save state, because six
 * independently-saved sections otherwise give an operator no way to see
 * which ones they have touched.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCustomer,
  patchCustomerConfig,
  previewCustomerConfig,
  listSftpKeys,
  addSftpKey,
  deleteSftpKey,
  rotateF2rCredentials,
  setSlackWebhook,
  clearSlackWebhook,
  testSlackWebhook,
  type CustomerDetail,
  type DryRunPreview,
  type SettingsPatchBody,
} from "../api/portal";
import { QueryError } from "../components/QueryError";
import { ReportTypeSelect, REPORT_TYPES } from "../components/ReportTypeSelect";
import { ConfirmDialog } from "../components/ConfirmDialog";

type Draft = Partial<CustomerDetail>;

const IDENTITY_FIELDS: readonly (keyof CustomerDetail)[] = [
  "client_name",
  "legal_name",
  "contact_email",
];

const FINTRAC_FIELDS: readonly (keyof CustomerDetail)[] = [
  "reporting_entity_number",
  "submitting_reporting_entity_number",
  "reporting_entity_contact_id",
  "activity_sector_code",
];

/** The full shape `patchCustomerConfig` resolves to, including `applied` --
 *  named here so a section can read fields beyond `customer` (e.g. the
 *  reporting section's real cancelled-candidate counts) without widening
 *  what every other section has to handle. */
type PatchResult = Awaited<ReturnType<typeof patchCustomerConfig>>;

/** One save per section. `fields` names what this section owns, so the
 *  PATCH body carries nothing the operator did not edit here.
 *
 *  `savedCustomer` is a per-hook-instance signal: it only changes when
 *  THIS section's own mutation succeeds, carrying the fresh record straight
 *  from the PATCH response. A section resets its own draft off this, never
 *  off the shared `["customer", clientId]` query -- that query is also
 *  invalidated (and refetched) after every section's save, so keying a
 *  reset on it would silently wipe in-progress edits in a sibling section
 *  whenever either one saved. */
function useSectionSave(
  clientId: string,
  customer: CustomerDetail,
  onTokenRefresh?: (updatedAt: string) => void,
) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [savedCustomer, setSavedCustomer] = useState<CustomerDetail | null>(null);
  const [lastApplied, setLastApplied] = useState<PatchResult["applied"] | null>(null);
  // Whether THIS section has saved at least once in this page visit -- the
  // footer's "Saved" state. It is not cleared on a timer: the footer shows
  // it only while the section is clean, so the next edit replaces it with
  // "Unsaved changes" on its own.
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: (body: SettingsPatchBody) => patchCustomerConfig(clientId, body),
    onSuccess: (result) => {
      setError(null);
      setSavedCustomer(result.customer);
      setLastApplied(result.applied);
      setSaved(true);
      // Every section's concurrency token is refreshed off THIS section's
      // own save response, not just this section's -- see the shared
      // `token` state in SettingsPage. Sections save independently and each
      // save bumps the record's shared `updated_at`, so without this a
      // second section's still-original token looks "changed by someone
      // else" purely because a sibling saved first, with no other user
      // involved at all.
      onTokenRefresh?.(result.customer.updated_at);
      queryClient.invalidateQueries({ queryKey: ["customer", clientId] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
    onError: (e: Error & { status?: number }) => {
      setSaved(false);
      setError(e.status === 409
        ? "This customer was changed by someone else. Reload and try again."
        : e.message);
    },
  });

  // `updatedAt` is passed in by the caller (the page-level shared token, see
  // `SettingsPageBody`) rather than read off `customer` here: `customer` is
  // the live, possibly-refetched query value (e.g. a window-focus refetch
  // can silently pull in someone else's concurrent save), but the
  // concurrency check must carry the most recently known COMMITTED version
  // -- which is refreshed only by an actual save response (above), never by
  // a bare refetch -- or a stale-focus refetch defeats the 409 check it
  // exists to trigger.
  //
  // `extra` carries fields that are never part of a draft/baseline diff --
  // currently just the reporting section's `cancel_in_flight`, which is a
  // one-time operator choice made at confirm time, not a persisted column.
  const save = (
    draft: Draft,
    fields: readonly (keyof CustomerDetail)[],
    updatedAt: string,
    extra?: Record<string, unknown>,
  ) => {
    const body: SettingsPatchBody = { updated_at: updatedAt, ...extra };
    for (const f of fields) {
      if (draft[f] !== undefined && draft[f] !== customer[f]) {
        (body as Record<string, unknown>)[f] = draft[f];
      }
    }
    mutation.mutate(body);
  };

  return { save, error, busy: mutation.isPending, saved, savedCustomer, lastApplied };
}

/* -------------------------------------------------------------------------
   Presentation primitives. Nothing here holds state -- they exist so the six
   sections below read as their fields rather than as six copies of the same
   card shell.
   ------------------------------------------------------------------------- */

function SectionCard(
  { title, description, children }:
  { title: string; description?: ReactNode; children: ReactNode },
) {
  return (
    <section className="card form-card settings-card">
      <header className="settings-card-head">
        <h2>{title}</h2>
        {description && <p className="settings-card-desc">{description}</p>}
      </header>
      {children}
    </section>
  );
}

/** The responsive field grid (one column narrow, two from 860px). Rows that
 *  must span it -- long values, the report-type menu with its descriptions --
 *  carry `full`. */
function Fields({ children }: { children: ReactNode }) {
  return <div className="form"><div className="form-section">{children}</div></div>;
}

function Row(
  { label, htmlFor, hint, full, children }:
  { label: ReactNode; htmlFor?: string; hint?: ReactNode; full?: boolean; children: ReactNode },
) {
  return (
    <div className={`form-row${full ? " full" : ""}`}>
      {htmlFor
        ? <label htmlFor={htmlFor}>{label}</label>
        : <span className="group-label">{label}</span>}
      {children}
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

function TextRow(
  { id, label, value, onChange, type = "text", hint, full, placeholder, mono }:
  {
    id: string; label: string; value: string;
    onChange: (v: string) => void;
    type?: string; hint?: ReactNode; full?: boolean; placeholder?: string; mono?: boolean;
  },
) {
  return (
    <Row label={label} htmlFor={id} hint={hint} full={full}>
      <input
        id={id}
        type={type}
        className={mono ? "mono" : undefined}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </Row>
  );
}

/** Card footer: this section's save state on the left, its button on the
 *  right. The button label is always the section's own ("Save identity", not
 *  "Save") -- six identically-named buttons on one page are ambiguous to a
 *  screen reader and to anyone scanning the page. Busy is shown in the
 *  status text rather than by relabelling the button, so the button's
 *  accessible name never moves. */
function SaveFooter(
  { label, dirty, busy, saved, note, onSave }:
  {
    label: string; dirty: boolean; busy?: boolean; saved?: boolean;
    note?: string | null; onSave: () => void;
  },
) {
  let tone = "";
  let text = "";
  if (busy) {
    text = "Saving…";
  } else if (dirty) {
    tone = " dirty";
    text = "Unsaved changes";
  } else if (note) {
    tone = " saved";
    text = note;
  } else if (saved) {
    tone = " saved";
    text = "Saved";
  }
  return (
    <div className="settings-card-foot">
      <span className={`settings-status${tone}`} role="status">{text}</span>
      <button className="btn-primary" type="button" disabled={busy || !dirty} onClick={onSave}>
        {label}
      </button>
    </div>
  );
}

function identityFields(c: CustomerDetail): Draft {
  return {
    client_name: c.client_name, legal_name: c.legal_name, contact_email: c.contact_email,
  };
}

type SectionProps = {
  clientId: string;
  customer: CustomerDetail;
  /** The page-level shared concurrency token -- see `SettingsPageBody`. */
  token: string;
  onSaved: (updatedAt: string) => void;
};

function IdentitySection({ clientId, customer, token, onSaved }: SectionProps) {
  const { save, error, busy, saved, savedCustomer } = useSectionSave(clientId, customer, onSaved);
  // `baseline` is this section's own last-known-committed values -- set on
  // mount and, below, after this section's own successful save. It is
  // deliberately NOT synced from the `customer` prop on every render: that
  // prop also changes after a SIBLING section's save (shared query
  // invalidation) or a window-focus refetch, and resetting off it would
  // discard whatever the operator is mid-typing here (or, for `updated_at`
  // specifically, defeat the 409 concurrency check -- see `save` above).
  const [baseline, setBaseline] = useState(() => identityFields(customer));
  const [draft, setDraft] = useState<Draft>(baseline);

  useEffect(() => {
    if (!savedCustomer) return;
    const next = identityFields(savedCustomer);
    setBaseline(next);
    setDraft(next);
  }, [savedCustomer]);

  const dirty = IDENTITY_FIELDS.some((f) => draft[f] !== baseline[f]);

  return (
    <SectionCard
      title="Identity"
      description="How this customer is named in the portal and on the reports filed for them."
    >
      <Fields>
        <TextRow
          id="settings-client-name"
          label="Display name"
          hint="Shown in the customer switcher and page headers."
          value={draft.client_name ?? ""}
          onChange={(v) => setDraft((d) => ({ ...d, client_name: v }))}
        />
        <TextRow
          id="settings-legal-name"
          label="Legal name"
          hint="The registered entity name, as filed with FINTRAC."
          value={draft.legal_name ?? ""}
          onChange={(v) => setDraft((d) => ({ ...d, legal_name: v || null }))}
        />
        <TextRow
          id="settings-contact-email"
          label="Contact email"
          type="email"
          full
          hint="Compliance contact on the client record. Not a portal login — invite portal users from the Team page."
          value={draft.contact_email ?? ""}
          onChange={(v) => setDraft((d) => ({ ...d, contact_email: v || null }))}
        />
      </Fields>
      {error && <p className="error" role="alert">{error}</p>}
      <SaveFooter
        label="Save identity"
        dirty={dirty}
        busy={busy}
        saved={saved}
        onSave={() => save(draft, IDENTITY_FIELDS, token)}
      />
    </SectionCard>
  );
}

function fintracFields(c: CustomerDetail): Draft {
  return {
    reporting_entity_number: c.reporting_entity_number,
    submitting_reporting_entity_number: c.submitting_reporting_entity_number,
    reporting_entity_contact_id: c.reporting_entity_contact_id,
    activity_sector_code: c.activity_sector_code,
  };
}

function FintracSection({ clientId, customer, token, onSaved }: SectionProps) {
  const { save, error, busy, saved, savedCustomer } = useSectionSave(clientId, customer, onSaved);
  // See the matching comment in IdentitySection: baseline tracks only this
  // section's own successful saves, not the shared customer prop.
  const [baseline, setBaseline] = useState(() => fintracFields(customer));
  const [draft, setDraft] = useState<Draft>(baseline);

  useEffect(() => {
    if (!savedCustomer) return;
    const next = fintracFields(savedCustomer);
    setBaseline(next);
    setDraft(next);
  }, [savedCustomer]);

  const dirty = FINTRAC_FIELDS.some((f) => draft[f] !== baseline[f]);

  return (
    <SectionCard
      title="FINTRAC"
      description="Identifiers stamped on every report filed for this customer. A wrong value here is rejected by FINTRAC at submission, not at ingestion."
    >
      <Fields>
        <TextRow
          id="settings-reporting-entity-number"
          label="Reporting entity number"
          mono
          value={draft.reporting_entity_number ?? ""}
          onChange={(v) => setDraft((d) => ({ ...d, reporting_entity_number: v || null }))}
        />
        <TextRow
          id="settings-submitting-reporting-entity-number"
          label="Submitting reporting entity number"
          mono
          hint="The entity that submits to FINTRAC. Often the same as above."
          value={draft.submitting_reporting_entity_number ?? ""}
          onChange={(v) => setDraft((d) => ({
            ...d, submitting_reporting_entity_number: v || null,
          }))}
        />
        <TextRow
          id="settings-reporting-entity-contact-id"
          label="Reporting entity contact ID"
          mono
          hint="Required on every report — reports cannot be generated without it."
          value={draft.reporting_entity_contact_id ?? ""}
          onChange={(v) => setDraft((d) => ({
            ...d, reporting_entity_contact_id: v || null,
          }))}
        />
        <Row label="Activity sector code" htmlFor="settings-activity-sector-code" hint="11 is MSB.">
          <input
            id="settings-activity-sector-code"
            type="number"
            className="mono"
            min={0}
            value={draft.activity_sector_code ?? ""}
            onChange={(e) => setDraft((d) => ({
              ...d, activity_sector_code: e.target.value === "" ? null : Number(e.target.value),
            }))}
          />
        </Row>
      </Fields>
      {error && <p className="error" role="alert">{error}</p>}
      <SaveFooter
        label="Save FINTRAC details"
        dirty={dirty}
        busy={busy}
        saved={saved}
        onSave={() => save(draft, FINTRAC_FIELDS, token)}
      />
    </SectionCard>
  );
}

const REPORTING_FIELDS: readonly (keyof CustomerDetail)[] = [
  "report_types", "threshold_cad", "eftr_directions",
  "deadline_alert_days", "window_start_time",
];

const EFTR_DIRECTIONS = ["Initiation", "Final receipt"] as const;

function reportingFields(c: CustomerDetail): Draft {
  return {
    report_types: c.report_types,
    threshold_cad: c.threshold_cad,
    eftr_directions: c.eftr_directions,
    deadline_alert_days: c.deadline_alert_days,
    window_start_time: c.window_start_time,
  };
}

/** Built from the dry-run preview, one line per consequence -- see spec §2.5
 *  and CLAUDE.md's note on this screen: an operator reads this once, under
 *  time pressure, before cancelling or letting go real FINTRAC filings. */
function reportingConsequences(p: DryRunPreview): ReactNode[] {
  const out: ReactNode[] = [];
  for (const [type, flight] of Object.entries(p.in_flight)) {
    const total = Object.values(flight.candidates).reduce((a, b) => a + b, 0);
    const breakdown = Object.entries(flight.candidates)
      .map(([status, n]) => `${n} ${status}`).join(", ");
    if (total) out.push(`${total} ${type} reports are in flight (${breakdown}).`);
    if (flight.pending_txn_rows) {
      out.push(
        `${flight.pending_txn_rows} ingested transactions are still awaiting ` +
        `detection and would keep producing ${type} reports for up to 48 hours.`);
    }
  }
  for (const add of p.newly_required_fields) {
    out.push(
      `Adding ${add.report_type} makes these mandatory at ingestion — files ` +
      `missing them will be rejected at ingestion: ${add.fields.join(", ")}.`);
  }
  return out;
}

/** Reporting settings, save-guarded by a `previewCustomerConfig` dry run
 *  rendered in a `ConfirmDialog` -- Save never applies directly. Two of the
 *  fields this section owns take effect only going forward (`report_types`),
 *  two apply immediately to everything not yet detected, including
 *  already-ingested transactions (`threshold_cad`, `eftr_directions`,
 *  `deadline_alert_days` -- the detector reads these off the live client row
 *  at run time). Removing a report type does not stop candidates already in
 *  flight; that is exactly what the confirm dialog's in-flight breakdown and
 *  cancel/let-file choice are for. See CLAUDE.md and spec §2.5/§5.1. */
function ReportingSection({ clientId, customer, token, onSaved }: SectionProps) {
  const { save, error, busy, saved, savedCustomer, lastApplied } =
    useSectionSave(clientId, customer, onSaved);
  // See the matching comment in IdentitySection: baseline tracks only this
  // section's own successful saves, not the shared customer prop.
  const [baseline, setBaseline] = useState(() => reportingFields(customer));
  const [draft, setDraft] = useState<Draft>(baseline);
  const [preview, setPreview] = useState<DryRunPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [cancelInFlight, setCancelInFlight] = useState(true);
  const [applied, setApplied] = useState<string | null>(null);

  useEffect(() => {
    if (!savedCustomer) return;
    const next = reportingFields(savedCustomer);
    setBaseline(next);
    setDraft(next);
  }, [savedCustomer]);

  // The real count, from the apply response's live rows -- the preview ran
  // in a separate Lambda invocation and the detector can create candidates
  // in between, so it is never authoritative for what actually got
  // cancelled. Spec §5.1.
  useEffect(() => {
    if (!lastApplied) return;
    const cancelled = lastApplied.cancelled;
    setApplied(cancelled
      ? `Saved. Cancelled ${cancelled.candidates} reports and released ${cancelled.txn_rows} transactions.`
      : null);
    setPreview(null);
  }, [lastApplied]);

  // A concurrent-edit (or other) failure on the real apply means there is
  // nothing left to confirm -- close the dialog so the operator sees the
  // error where every other section shows it, instead of it being hidden
  // behind the still-open modal.
  useEffect(() => {
    if (error) setPreview(null);
  }, [error]);

  const dirty = REPORTING_FIELDS.some((f) => draft[f] !== baseline[f]);

  // `ReportTypeSelect` only offers EFTR/LCTR/LVCTR/STR -- CDR is excluded
  // because cdr_builder.py is a deferred stub. `onboard_client.sh` validates
  // nothing, so a CLI-onboarded client can hold a stored type the selector
  // cannot render as checked; without this warning, opening and saving this
  // section would silently drop it.
  const unrenderable = customer.report_types.filter(
    (t) => !REPORT_TYPES.some((known) => known.code === t));

  function reportingBody(): SettingsPatchBody {
    const body: SettingsPatchBody = { updated_at: token };
    for (const f of REPORTING_FIELDS) {
      if (draft[f] !== undefined && draft[f] !== customer[f]) {
        (body as Record<string, unknown>)[f] = draft[f];
      }
    }
    return body;
  }

  async function onSaveReporting() {
    setPreviewError(null);
    setPreviewBusy(true);
    try {
      setPreview(await previewCustomerConfig(clientId, reportingBody()));
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : "Failed to preview these changes.");
    } finally {
      setPreviewBusy(false);
    }
  }

  function onConfirm() {
    save(draft, REPORTING_FIELDS, token, { cancel_in_flight: cancelInFlight });
  }

  const hasInFlight = !!preview && Object.keys(preview.in_flight).length > 0;

  return (
    <SectionCard
      title="Reporting"
      description={
        "Report types apply to transactions ingested from now on; they are not "
        + "retroactive. Threshold, directions, and alert days apply to everything "
        + "not yet detected, including transactions already ingested. Saving here "
        + "always previews the blast radius before anything changes."
      }
    >
      {unrenderable.length > 0 && (
        <p className="warning" role="alert">
          This customer is configured for {unrenderable.join(", ")}, which this form
          cannot edit. Saving this section will remove {unrenderable.length === 1 ? "it" : "them"}.
          Use the CLI if you need to keep {unrenderable.length === 1 ? "it" : "them"}.
        </p>
      )}
      <Fields>
        <Row
          label="Report types"
          htmlFor="settings-report-types"
          full
          hint="Which reports the pipeline generates for this customer."
        >
          <ReportTypeSelect
            id="settings-report-types"
            value={draft.report_types ?? []}
            onChange={(next) => setDraft((d) => ({ ...d, report_types: next }))}
          />
        </Row>
        <Row
          label="Threshold (CAD)"
          htmlFor="settings-threshold-cad"
          hint="Reporting threshold used by the detectors."
        >
          <div className="input-affix">
            <span className="affix" aria-hidden="true">$</span>
            <input
              id="settings-threshold-cad"
              type="text"
              inputMode="decimal"
              value={draft.threshold_cad ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, threshold_cad: e.target.value }))}
            />
          </div>
        </Row>
        <Row
          label="Window start time (HH:MM)"
          htmlFor="settings-window-start-time"
          hint="24-hour clock, Eastern Time. Start of the aggregation window."
        >
          <input
            id="settings-window-start-time"
            type="text"
            className="mono"
            placeholder="00:00"
            value={draft.window_start_time ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, window_start_time: e.target.value }))}
          />
        </Row>
        <Row
          label="EFTR directions"
          hint="Which side of a wire transfer this customer reports."
        >
          <div className="check-list" role="group" aria-label="EFTR directions">
            {EFTR_DIRECTIONS.map((dir) => (
              <label key={dir}>
                <input
                  type="checkbox"
                  checked={(draft.eftr_directions ?? []).includes(dir)}
                  onChange={(e) => setDraft((d) => {
                    const current = d.eftr_directions ?? [];
                    const next = e.target.checked
                      ? [...current, dir]
                      : current.filter((x) => x !== dir);
                    return { ...d, eftr_directions: next };
                  })}
                />
                {dir}
              </label>
            ))}
          </div>
        </Row>
        <Row
          label="Deadline alert days"
          htmlFor="settings-deadline-alert-days"
          hint="How many days before a filing deadline the monitor alerts."
        >
          <input
            id="settings-deadline-alert-days"
            type="number"
            className="mono"
            min={0}
            value={draft.deadline_alert_days ?? ""}
            onChange={(e) => setDraft((d) => ({
              ...d, deadline_alert_days: e.target.value === "" ? 0 : Number(e.target.value),
            }))}
          />
        </Row>
      </Fields>
      {error && <p className="error" role="alert">{error}</p>}
      {previewError && <p className="error" role="alert">{previewError}</p>}
      <SaveFooter
        label="Save reporting"
        dirty={dirty}
        busy={busy || previewBusy}
        saved={saved}
        note={applied}
        onSave={onSaveReporting}
      />

      {preview && (
        <ConfirmDialog
          title="Apply reporting changes?"
          consequences={reportingConsequences(preview)}
          confirmLabel="Apply changes"
          tone={hasInFlight ? "danger" : "normal"}
          busy={busy}
          onCancel={() => setPreview(null)}
          onConfirm={onConfirm}
          extra={hasInFlight ? (
            <fieldset className="choice">
              <legend>In-flight reports for the removed type</legend>
              <label>
                <input
                  type="radio"
                  name="in-flight"
                  checked={cancelInFlight}
                  onChange={() => setCancelInFlight(true)}
                />
                Cancel these — they will not be filed with FINTRAC
              </label>
              <label>
                <input
                  type="radio"
                  name="in-flight"
                  checked={!cancelInFlight}
                  onChange={() => setCancelInFlight(false)}
                />
                Let them file — finish and submit as configured before this change
              </label>
              <p className="hint">
                Cancelled reports cannot be undone from the portal. Re-adding the
                report type does not bring them back.
              </p>
            </fieldset>
          ) : undefined}
        />
      )}
    </SectionCard>
  );
}

/** SSH keys live in AWS Transfer, not the `clients` row -- a separate query
 *  so a Transfer outage degrades to an error inside this list, not a blank
 *  settings page. Also owns the add/remove flows since neither goes through
 *  `useSectionSave` (they are their own endpoints, not a `clients` PATCH). */
function SftpKeysList({ clientId }: { clientId: string }) {
  const queryClient = useQueryClient();
  const { data, error, isLoading } = useQuery({
    queryKey: ["sftp-keys", clientId],
    queryFn: () => listSftpKeys(clientId),
  });
  const [newKey, setNewKey] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [confirmingKeyId, setConfirmingKeyId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const addMutation = useMutation({
    mutationFn: (publicKey: string) => addSftpKey(clientId, publicKey),
    onSuccess: () => {
      setNewKey("");
      setAddError(null);
      queryClient.invalidateQueries({ queryKey: ["sftp-keys", clientId] });
    },
    onError: (e: Error) => setAddError(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (keyId: string) => deleteSftpKey(clientId, keyId),
    onSuccess: () => {
      setConfirmingKeyId(null);
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ["sftp-keys", clientId] });
    },
    onError: (e: Error) => setDeleteError(e.message),
  });

  const keys = data?.keys ?? [];
  const confirmingKey = keys.find((k) => k.key_id === confirmingKeyId);

  return (
    <div className="settings-keys">
      <div className="settings-subhead">
        <h3>SSH keys</h3>
        <span className="hint">Any key listed here can authenticate as {clientId}.</span>
      </div>
      {isLoading && <p className="empty">Loading keys…</p>}
      {error && (
        <>
          {/* QueryError gives the generic "couldn't load this" treatment;
              the raw message underneath is what actually tells an operator
              this is a Transfer outage, not an empty key list. */}
          <QueryError error={error} what="the SSH key list" />
          <p className="hint">{error.message}</p>
        </>
      )}
      {!error && !isLoading && (
        keys.length === 0 ? (
          <p className="empty">No SSH keys on file — this customer cannot connect to SFTP.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Key ID</th>
                <th>Imported</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.key_id}>
                  <td>{k.key_id}</td>
                  <td className={k.date_imported ? undefined : "empty-cell"}>
                    {k.date_imported
                      ? new Date(k.date_imported).toLocaleDateString()
                      : "unknown"}
                  </td>
                  <td className="key-actions">
                    <button type="button" onClick={() => setConfirmingKeyId(k.key_id)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
      {deleteError && <p className="error" role="alert">{deleteError}</p>}

      <div className="form">
        <div className="form-section">
          <Row
            label="Add SSH public key"
            htmlFor="settings-new-sftp-key"
            full
            hint="One OpenSSH public key, e.g. ssh-ed25519 AAAA…"
          >
            <textarea
              id="settings-new-sftp-key"
              placeholder="ssh-ed25519 AAAA…"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
            />
          </Row>
        </div>
      </div>
      {addError && <p className="error" role="alert">{addError}</p>}
      <div className="form-actions">
        <button
          className="btn-primary"
          type="button"
          disabled={addMutation.isPending || !newKey.trim()}
          onClick={() => addMutation.mutate(newKey.trim())}
        >
          Add key
        </button>
      </div>

      {confirmingKey && (
        <ConfirmDialog
          title="Remove SSH key?"
          tone="danger"
          confirmLabel="Remove key"
          busy={deleteMutation.isPending}
          consequences={[
            `Key ${confirmingKey.key_id} will no longer be accepted for SFTP login.`,
            ...(keys.length === 1
              ? ["This is the customer's only key on file — removing it locks " +
                 "them out of SFTP entirely until a new key is added."]
              : []),
          ]}
          onCancel={() => setConfirmingKeyId(null)}
          onConfirm={() => deleteMutation.mutate(confirmingKey.key_id)}
        />
      )}
    </div>
  );
}

/** SFTP IP allow-list. Saving is two steps server-side: the DB write always
 *  happens, then a security-group reconcile that is deliberately non-fatal
 *  (a firewall hiccup must never roll back a saved config) -- see CLAUDE.md
 *  "SFTP network access is per-client at runtime". `applied.warnings` is the
 *  only signal that the reconcile didn't happen, so it must be rendered, not
 *  just a generic "Saved." */
function AccessSection({ clientId, customer, token, onSaved }: SectionProps) {
  const { save, error, busy, saved, savedCustomer, lastApplied } =
    useSectionSave(clientId, customer, onSaved);
  // This section's own values baseline -- see the comment on `save` in
  // useSectionSave. The concurrency token itself is the page-level shared
  // `token` prop, not tracked here.
  const [baseline, setBaseline] = useState({ cidrs: customer.sftp_allowed_cidrs ?? [] });
  const [cidrsText, setCidrsText] = useState((customer.sftp_allowed_cidrs ?? []).join(", "));
  const [warnings, setWarnings] = useState<string[] | null>(null);

  useEffect(() => {
    if (!savedCustomer) return;
    setBaseline({ cidrs: savedCustomer.sftp_allowed_cidrs ?? [] });
    setCidrsText((savedCustomer.sftp_allowed_cidrs ?? []).join(", "));
  }, [savedCustomer]);

  useEffect(() => {
    if (!lastApplied) return;
    setWarnings(lastApplied.warnings && lastApplied.warnings.length > 0 ? lastApplied.warnings : null);
  }, [lastApplied]);

  const parsedCidrs = cidrsText.split(",").map((s) => s.trim()).filter(Boolean);
  const dirty = JSON.stringify(parsedCidrs) !== JSON.stringify(baseline.cidrs);

  function onSave() {
    save({ sftp_allowed_cidrs: parsedCidrs }, ["sftp_allowed_cidrs"], token);
  }

  return (
    <SectionCard
      title="Access"
      description={
        "Who can reach SFTP, and with which keys. Saving IP ranges always updates "
        + "the database; the firewall reconcile that actually opens the port is "
        + "separate and best-effort, so a failure there does not undo the save — it "
        + "appears below as a warning."
      }
    >
      <Fields>
        <TextRow
          id="settings-sftp-cidrs"
          label="Allowed SFTP IP ranges"
          full
          mono
          placeholder="203.0.113.0/24, 198.51.100.7/32"
          hint="Comma-separated CIDR ranges. 0.0.0.0/0 is rejected. Leave empty and the customer can authenticate but every connection is blocked at the firewall."
          value={cidrsText}
          onChange={setCidrsText}
        />
      </Fields>
      {error && <p className="error" role="alert">{error}</p>}
      {warnings && (
        <div className="warning" role="alert">
          <strong>Saved, but the firewall did not update:</strong>
          <ul>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}
      <SaveFooter
        label="Save access"
        dirty={dirty}
        busy={busy}
        saved={saved}
        onSave={onSave}
      />

      <SftpKeysList clientId={clientId} />
    </SectionCard>
  );
}

/** FINTRAC OAuth client credentials. Write-only end to end: the API never
 *  returns a current value, so these two inputs never have anything to
 *  prefill and are cleared (not re-displayed) after a successful rotation.
 *  See CLAUDE.md: `fn_submit_fintrac` caches the OAuth token module-level per
 *  client, so an already-warm Lambda keeps using the old token until it
 *  expires -- the confirm dialog says so explicitly, or an operator watching
 *  a submission succeed right after rotating could conclude it failed (or
 *  rotate again unnecessarily). */
function SecretsSection({ clientId }: { clientId: string }) {
  const [creds, setCreds] = useState({ oauth_client_id: "", oauth_client_secret: "" });
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rotated, setRotated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onRotate() {
    setBusy(true);
    try {
      await rotateF2rCredentials(clientId, creds);
      // Cleared, never echoed back: the portal must not become a second
      // place the secret can be read.
      setCreds({ oauth_client_id: "", oauth_client_secret: "" });
      setConfirming(false);
      setRotated(true);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to rotate credentials.");
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  const ready = !!creds.oauth_client_id && !!creds.oauth_client_secret;
  // Only once one field is filled -- an untouched section should not open
  // with a complaint about fields nobody has tried to submit.
  const partial = !ready && (!!creds.oauth_client_id || !!creds.oauth_client_secret);

  return (
    <SectionCard
      title="Secrets"
      description="The customer's FINTRAC F2R OAuth credentials. Current values are never shown — they are write-only in Secrets Manager, so rotating replaces them without a way to read what was there."
    >
      <Fields>
        <TextRow
          id="settings-oauth-client-id"
          label="New OAuth client ID"
          mono
          value={creds.oauth_client_id}
          onChange={(v) => {
            setCreds((c) => ({ ...c, oauth_client_id: v }));
            setRotated(false);
          }}
        />
        <TextRow
          id="settings-oauth-client-secret"
          label="New OAuth client secret"
          type="password"
          value={creds.oauth_client_secret}
          onChange={(v) => {
            setCreds((c) => ({ ...c, oauth_client_secret: v }));
            setRotated(false);
          }}
        />
      </Fields>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="settings-card-foot">
        <span className={`settings-status${rotated ? " saved" : ""}`} role="status">
          {rotated ? "Credentials rotated." : partial ? "Both fields are required." : ""}
        </span>
        {/* The trigger is unmounted while the dialog is open, not merely
            disabled: leaving it mounted puts two "Rotate…" buttons on the
            page at once, which is ambiguous to a screen reader working the
            modal. */}
        {!confirming && (
          <button
            className="btn-primary"
            type="button"
            disabled={!ready}
            onClick={() => setConfirming(true)}
          >
            Rotate credentials
          </button>
        )}
      </div>

      {confirming && (
        <ConfirmDialog
          title="Rotate FINTRAC credentials?"
          tone="danger"
          confirmLabel="Rotate"
          busy={busy}
          consequences={[
            "The client's existing FINTRAC credentials stop working immediately.",
            "Submissions already holding a cached OAuth token keep using it " +
            "until it expires — rotation is not instantaneous.",
            "The current values are not recoverable from the portal.",
          ]}
          onCancel={() => setConfirming(false)}
          onConfirm={onRotate}
        />
      )}
    </SectionCard>
  );
}

const SLACK_PREFIX = "https://hooks.slack.com/services/";

/** The customer's channel in Scadox's own Slack workspace. Write-only: the
 *  webhook is a bearer credential, so the portal shows only its last four
 *  characters and never re-displays what was typed. Spec 2026-10-01 §6. */
function SlackSection({ clientId, customer }: { clientId: string; customer: CustomerDetail }) {
  const [url, setUrl] = useState("");
  const [hint, setHint] = useState(customer.slack_webhook_hint);
  const [status, setStatus] = useState(customer.slack_webhook_status);
  const [busy, setBusy] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const valid = url.startsWith(SLACK_PREFIX) && url.length > SLACK_PREFIX.length && !/\s/.test(url);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await action();
    } catch (e) {
      // Close the dialog too, or the alert stays hidden behind the modal.
      setConfirmingClear(false);
      setError(e instanceof Error ? e.message : "Request failed.");
    } finally {
      setBusy(false);
    }
  }

  const onSave = () => run(async () => {
    const r = await setSlackWebhook(clientId, url);
    setUrl("");
    setHint(r.hint);
    setStatus("ok");
    setMessage("Channel saved — a confirmation message was posted to it.");
  });
  const onTest = () => run(async () => {
    const r = await testSlackWebhook(clientId);
    setStatus(r.delivered ? "ok" : r.error?.startsWith("HTTP 4") ? "broken" : status);
    setMessage(r.delivered ? "Test message delivered." : `Test failed: ${r.error ?? "unknown error"}`);
  });
  const onClear = () => run(async () => {
    await clearSlackWebhook(clientId);
    setHint(null);
    setStatus(null);
    setConfirmingClear(false);
    setMessage("Channel removed.");
  });

  return (
    <SectionCard
      title="Slack notifications"
      description="Where this customer's alerts are posted in Scadox's Slack workspace. Create an incoming webhook for the channel in Slack, then paste it here. Changes take effect within five minutes."
    >
      <p className="settings-sub" role="status">
        {!hint && "No Slack channel — notifications go to the default channel."}
        {hint && status === "broken" &&
          `Channel …${hint} is broken — notifications go to the default channel until it is replaced or a test succeeds.`}
        {hint && status !== "broken" && `Channel set (…${hint}).`}
      </p>
      <Fields>
        <TextRow
          id="settings-slack-url"
          label="Slack incoming webhook URL"
          type="text"
          mono
          value={url}
          onChange={(v) => setUrl(v.trim())}
        />
      </Fields>
      {url && !valid && <p className="error">Must start with {SLACK_PREFIX}</p>}
      {error && <p className="error" role="alert">{error}</p>}
      <div className="settings-card-foot">
        <span className="settings-status" role="status">{message ?? ""}</span>
        {hint && !confirmingClear && (
          <>
            <button type="button" disabled={busy} onClick={onTest}>Send test message</button>
            <button type="button" disabled={busy} onClick={() => setConfirmingClear(true)}>
              Remove channel
            </button>
          </>
        )}
        {!confirmingClear && (
          <button className="btn-primary" type="button" disabled={!valid || busy} onClick={onSave}>
            Save channel
          </button>
        )}
      </div>
      {confirmingClear && (
        <ConfirmDialog
          title="Remove this Slack channel?"
          tone="danger"
          confirmLabel="Remove"
          busy={busy}
          consequences={[
            "This customer's notifications go to the default channel instead.",
            "The webhook is deleted from Secrets Manager and cannot be recovered from the portal.",
          ]}
          onCancel={() => setConfirmingClear(false)}
          onConfirm={onClear}
        />
      )}
    </SectionCard>
  );
}


/** Read-only facts about the record. A definition list rather than disabled
 *  inputs: none of these are editable here, and a greyed-out input invites an
 *  operator to try. */
function IdentifiersSection({ customer }: { customer: CustomerDetail }) {
  return (
    <SectionCard
      title="Identifiers"
      description="Set at onboarding. Changing any of these needs engineering."
    >
      <dl className="settings-idents">
        <div>
          <dt>Client ID</dt>
          <dd>{customer.client_id}</dd>
        </div>
        <div>
          <dt>SFTP username</dt>
          <dd>
            {customer.sftp_username}
            <span className="hint"> — contact engineering to rename</span>
          </dd>
        </div>
        <div>
          <dt>Created</dt>
          <dd>{new Date(customer.created_at).toLocaleString()}</dd>
        </div>
        <div>
          <dt>Cognito app client</dt>
          {customer.cognito_app_client_id
            ? <dd>{customer.cognito_app_client_id}</dd>
            : <dd className="empty-value">none — API not enabled</dd>}
        </div>
        <div>
          <dt>Customer Admin</dt>
          {customer.admin_email
            ? <dd>{customer.admin_email}</dd>
            : <dd className="empty-value">none</dd>}
        </div>
      </dl>
    </SectionCard>
  );
}

/** Owns the page's shared concurrency token, once `customer` is known to
 *  exist -- a separate component (rather than state in `SettingsPage`
 *  itself) so `useState(() => customer.updated_at)` initialises exactly
 *  once, from the first load, and is never re-initialised by a later
 *  refetch of the same query. */
function SettingsPageBody({ clientId, customer }: { clientId: string; customer: CustomerDetail }) {
  // The most recently known COMMITTED `updated_at` for this client, shared
  // by every section. Refreshed only when one of THIS page's own section
  // saves succeeds (`onSaved` below, threaded into `useSectionSave` in each
  // section) -- never from `customer` refetching on its own (e.g. a
  // window-focus refetch), which would silently pull in a different user's
  // edit and defeat the very check this token exists for. Section *values*
  // stay separate, per-section (each section's own `baseline`) -- only this
  // token is shared, which is what stops "save Identity, then save FINTRAC"
  // from producing a false "changed by someone else" on the second save.
  const [token, setToken] = useState(customer.updated_at);

  return (
    <div className="settings-page" data-testid="settings-page">
      <header className="settings-header">
        <h1>{customer.client_name}</h1>
        <p className="settings-sub">
          <span className="mono">{customer.client_id}</span>
          <span className="sep" aria-hidden="true">·</span>
          <span>Created {new Date(customer.created_at).toLocaleDateString()}</span>
          <span className={`badge ${customer.active ? "ok" : "inactive"}`}>
            {customer.active ? "Active" : "Inactive"}
          </span>
          <span className={`badge ${customer.api_enabled ? "ok" : "off"}`}>
            {customer.api_enabled ? "API enabled" : "API off"}
          </span>
        </p>
      </header>

      <IdentitySection clientId={clientId} customer={customer} token={token} onSaved={setToken} />
      <FintracSection clientId={clientId} customer={customer} token={token} onSaved={setToken} />
      <ReportingSection clientId={clientId} customer={customer} token={token} onSaved={setToken} />
      <AccessSection clientId={clientId} customer={customer} token={token} onSaved={setToken} />
      <SecretsSection clientId={clientId} />
      <SlackSection clientId={clientId} customer={customer} />
      <IdentifiersSection customer={customer} />
    </div>
  );
}

export function SettingsPage({ clientId }: { clientId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["customer", clientId],
    queryFn: () => getCustomer(clientId),
  });

  if (isLoading) return <p className="empty">Loading…</p>;
  if (error) return <QueryError error={error} what="this customer's settings" />;

  return <SettingsPageBody clientId={clientId} customer={data!.customer} />;
}
